import { DeliveryType, Order, OrderStatus } from '@api/Orders/Order';
import OrderServ from '@api/Orders/OrderServ';
import { PaymentMethod } from '@api/Orders/OrderPayments';

class OrdersService {
    saveOrder = (order: Order): Promise<Order> => {
        return new Promise(async (resolve, reject) => {
            try {
                const data = await OrderServ.saveOrder(order);
                resolve(data);
            } catch (error) {
                reject('Error saving order: ' + error);
            }
        });
    };

    getOrders = (
        status?: OrderStatus,
        startDate?: Date,
        endDate?: Date,
        branchOfficeId?: string,
        salesChannelId?: string,
        deliveryType?: DeliveryType,
        includeCollaboratorData: boolean = true
    ): Promise<Order[]> => {
        return new Promise(async (resolve, reject) => {
            try {
                const data = await OrderServ.getOrders(
                    status,
                    startDate,
                    endDate,
                    branchOfficeId,
                    salesChannelId,
                    deliveryType,
                    includeCollaboratorData
                );
                resolve(data);
            } catch (error) {
                console.log(error);
                reject('Error getting orders: ' + error);
            }
        });
    };

    getOrdersRealtime = (
        callback: (orders: Order[]) => void,
        startDate?: Date,
        endDate?: Date,
        branchOfficeId?: string
    ): (() => void) => {
        return OrderServ.getOrdersRealtime(callback, startDate, endDate, branchOfficeId);
    };

    getPendingPaymentOrders = (branchOfficeId: string): Promise<Order[]> => {
        return new Promise(async (resolve, reject) => {
            try {
                const data = await OrderServ.getPendingPaymentOrders(branchOfficeId);
                resolve(data);
            } catch (error) {
                console.log(error);
                reject('getPendingPaymentOrders: ' + error);
            }
        });
    };

    getOrder = (orderId: string): Promise<Order> => {
        return new Promise(async (resolve, reject) => {
            try {
                const order = await OrderServ.getOrder(orderId);
                resolve(order);
            } catch (error) {
                reject('Error getting order: ' + error);
            }
        });
    };

    deleteOrder = (orderId: string): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await OrderServ.deleteOrder(orderId);
                resolve();
            } catch (error) {
                reject('Error deleting order: ' + error);
            }
        });
    };

    // Borrado de limpieza: además del doc, deshace caja/mesa/puntos (ver OrderServ.purgeOrder)
    purgeOrder = (
        orderId: string
    ): Promise<{ cashFlowsDeleted: number; tableReleased: boolean; pointsRebuilt: boolean }> => {
        return new Promise(async (resolve, reject) => {
            try {
                const result = await OrderServ.purgeOrder(orderId);
                resolve(result);
            } catch (error) {
                reject('Error deleting order: ' + error);
            }
        });
    };

    purgeMultipleOrders = (orderIds: string[]): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await OrderServ.purgeMultipleOrders(orderIds);
                resolve();
            } catch (error) {
                reject('Error deleting orders: ' + error);
            }
        });
    };

    payOrder = (
        orderId: string,
        cashAmount: number,
        creditCardAmount: number,
        bankTransferAmount: number
    ): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await OrderServ.payOrder(orderId, cashAmount, creditCardAmount, bankTransferAmount);
                resolve();
            } catch (error) {
                reject('Error paying order: ' + error);
            }
        });
    };

    registerCollectedPayment = (orderId: string, method: PaymentMethod, amount: number): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await OrderServ.registerCollectedPayment(orderId, method, amount);
                resolve();
            } catch (error) {
                reject('Error registering payment: ' + error);
            }
        });
    };

    logCompletedWithoutPayment = (orderId: string): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await OrderServ.logCompletedWithoutPayment(orderId);
                resolve();
            } catch (error) {
                reject('Error logging completion without payment: ' + error);
            }
        });
    };

    completeOrder = (orderId: string): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await OrderServ.completeOrder(orderId);
                resolve();
            } catch (error) {
                reject('Error completing order: ' + error);
            }
        });
    };

    cancelOrder = (orderId: string): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await OrderServ.cancelOrder(orderId);
                resolve();
            } catch (error) {
                reject('Error canceling order: ' + error);
            }
        });
    };

    // `previous` = el pedido ANTES de editarlo: con él la bitácora dice qué
    // cambió en vez de un "modificó este pedido" que no sirve para auditar.
    updateOrder = (order: Order, previous?: Order | null): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await OrderServ.updateOrder(order, previous);
                resolve();
            } catch (error) {
                reject('Error updating order: ' + error);
            }
        });
    };

    updateStatus = (orderId: string, status: OrderStatus): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await OrderServ.updateStatus(orderId, status);
                resolve();
            } catch (error) {
                reject('Error updating order status: ' + error);
            }
        });
    };

    routeOrder = (orderId: string, routeUrl?: string): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await OrderServ.routeOrder(orderId, routeUrl);
                resolve();
            } catch (error) {
                reject('Error routing order: ' + error);
            }
        });
    };

    deleteMultipleOrders = (orderIds: string[]): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                await OrderServ.deleteMultipleOrders(orderIds);
                resolve();
            } catch (error) {
                reject('Error deleting multiple orders: ' + error);
            }
        });
    };

    sendMessageTicket = (orderId: string, manual: boolean = false): Promise<{ skipped?: string }> => {
        return new Promise(async (resolve, reject) => {
            try {
                resolve(await OrderServ.sendMessageTicket(orderId, manual));
            } catch (error) {
                reject('sendTicket: ' + error);
            }
        });
    };

    // TABLES

    getOrdersByTable = (tableKioskId: string): Promise<Order[]> => {
        return new Promise(async (resolve, reject) => {
            try {
                const order = await OrderServ.getOrdersByTable(tableKioskId);
                resolve(order);
            } catch (error) {
                reject('getOrdersByTable: ' + error);
            }
        });
    };

    // customerUid opcional: cerrar SOLO la cuenta de ese cliente en la mesa
    completeTableOrders = (tableKioskId: string, customerUid?: string): Promise<Order> => {
        return new Promise(async (resolve, reject) => {
            try {
                const order = await OrderServ.completeTableOrders(tableKioskId, customerUid);
                resolve(order);
            } catch (error) {
                reject('completeTableOrders: ' + error);
            }
        });
    };
}
export default OrdersService;
